import type { FormInstance } from "antd";

export type ApiFieldError = {
  field: string;
  message: string;
};

/**
 * Map server field-level errors back to the form so each error appears under
 * its input. Use inside a mutation's `onError`:
 *
 *   const [form] = Form.useForm<EditUserFormValues>();
 *   const setFieldErrors = Form.useApiFormErrors(form);
 *
 *   const mutation = useMutation({
 *     mutationFn: (values) => api.updateUser(id, values),
 *     onError: (err) => {
 *       if (err.fieldErrors) {
 *         setFieldErrors(err.fieldErrors);
 *         return;
 *       }
 *       message.error(getApiErrorMessage(err, "Could not save"));
 *     },
 *   });
 *
 * The backend payload should be an array of `{ field, message }`. Adapt to
 * whatever shape your API returns by transforming the array before calling.
 */
export function useApiFormErrors<Values = unknown>(
  form: FormInstance<Values>,
) {
  return (errors: ApiFieldError[]) => {
    form.setFields(
      // antd's typed `name` path is the dotted form path; runtime strings
      // from the server are passed through with a cast.
      errors.map(
        (e) =>
          ({ name: e.field, errors: [e.message] }) as Parameters<
            typeof form.setFields
          >[0][number],
      ),
    );
  };
}
